import { useEffect } from 'react';
import SEOHead from '../../components/feature/SEOHead';

export default function Charters() {
  const baseUrl = import.meta.env.VITE_SITE_URL || 'https://crownabundance.com';
  
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': 'Charter Forums | Wealth Relationships Legacy',
    'description': 'Explore The CROWN Abundance Movement three charter pillars: Wealth Mastery, Relationship Wholeness, and Generational Legacy. Join specialized forums for entrepreneurs, founders, investors, families, and youth.',
    'url': `${baseUrl}/charters`,
    'mainEntity': {
      '@type': 'ItemList',
      'itemListElement': [
        {
          '@type': 'Thing',
          'name': 'Wealth Mastery Charter',
          'description': 'Economic stewardship, enterprise development, financial dominion'
        },
        {
          '@type': 'Thing',
          'name': 'Relationship Wholeness Charter',
          'description': 'Emotional intelligence, covenantal commitment, relational healing'
        },
        {
          '@type': 'Thing',
          'name': 'Generational Legacy Charter',
          'description': 'Mentorship, cultural influence, heritage transfer, identity building'
        }
      ]
    }
  };

  useEffect(() => {
    document.title = 'Charter Forums | Wealth Relationships Legacy | The CROWN Abundance Movement';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Explore The CROWN Abundance Movement three charter pillars: Wealth Mastery, Relationship Wholeness, and Generational Legacy. Join specialized forums for entrepreneurs, founders, investors, families, and youth.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'wealth mastery charter, relationship wholeness charter, generational legacy charter, founders forum, entrepreneurs forum, investors forum, singles forum, married forum');
    }
  }, []);

  const charters = [
    {
      id: 'wealth',
      title: 'Wealth Mastery Charter',
      subtitle: 'Economic stewardship, enterprise development, financial dominion',
      icon: 'ri-money-dollar-circle-line',
      gradient: 'from-emerald-400 to-teal-500',
      bgImage: 'https://public.readdy.ai/ai/img_res/1f90253fc13d8f86074eccb0f4333645.jpg',
      forums: [
        {
          name: 'Founders Forum',
          description: 'Leaders of already operational and successful businesses. Focus is on excellence, innovation, succession, and industry leadership, not early-stage scaling.',
          icon: 'ri-building-line',
          focus: 'Business Excellence & Succession'
        },
        {
          name: 'Entrepreneurs Forum',
          description: 'Inclusive of aspiring business owners, sole proprietors, early-stage founders, and those in mentorship or incubation phases.',
          icon: 'ri-rocket-line',
          focus: 'Launch & Growth Support'
        },
        {
          name: 'Investors Forum',
          description: 'Capital allocators and ethical financiers focused on impact-aligned investment and kingdom economics.',
          icon: 'ri-line-chart-line',
          focus: 'Impact-Aligned Investing'
        }
      ]
    },
    {
      id: 'relationships',
      title: 'Relationship Wholeness Charter',
      subtitle: 'Emotional intelligence, covenantal commitment, relational healing',
      icon: 'ri-heart-line',
      gradient: 'from-rose-400 to-pink-500',
      bgImage: 'https://readdy.ai/api/search-image?query=Beautiful%20diverse%20African%20American%20and%20Latino%20couple%20in%20loving%20embrace%2C%20warm%20golden%20lighting%2C%20elegant%20modern%20living%20room%20setting%2C%20romantic%20atmosphere%20representing%20relationship%20healing%20and%20wholeness%2C%20luxury%20wellness%20environment%2C%20no%20white%20people%2C%20only%20Black%20and%20Latino%20people&width=800&height=400&seq=relationship-charter-v4&orientation=landscape',
      forums: [
        {
          name: 'Singles Forum',
          description: 'Personal formation, emotional healing, preparation for marriage, and building healthy relationship foundations.',
          icon: 'ri-user-line',
          focus: 'Personal Formation & Healing'
        },
        {
          name: 'Married Forum',
          description: 'Strengthening covenant partnerships, legacy parenting, communication mastery, and building thriving families.',
          icon: 'ri-heart-2-line',
          focus: 'Covenant Partnership Building'
        },
        {
          name: 'Post-Marriage Forum',
          description: 'Healing and restoration after divorce, widowhood, or separation. A safe space for rebuilding and renewal.',
          icon: 'ri-heart-add-line',
          focus: 'Restoration & Renewal'
        }
      ]
    },
    {
      id: 'legacy',
      title: 'Generational Legacy Charter',
      subtitle: 'Mentorship, cultural influence, heritage transfer, identity building',
      icon: 'ri-parent-line',
      gradient: 'from-purple-400 to-indigo-500',
      bgImage: 'https://readdy.ai/api/search-image?query=Multi-generational%20African%20American%20and%20Latino%20family%20gathering%20in%20elegant%20library%20setting%20with%20grandfather%20teaching%20young%20adults%2C%20books%2C%20warm%20golden%20lighting%2C%20legacy%20and%20wisdom%20transfer%20atmosphere%2C%20sophisticated%20family%20environment%2C%20diverse%20people%20of%20color%2C%20no%20white%20people&width=800&height=400&seq=legacy-charter-v3&orientation=landscape',
      forums: [
        {
          name: 'Fathers Forum',
          description: 'Developing male leadership in home, society, and faith. Equipping men to lead with integrity and purpose.',
          icon: 'ri-parent-line',
          focus: 'Male Leadership Development'
        },
        {
          name: 'Youth Forum',
          description: 'Empowering teens and young adults through vision casting, financial literacy, identity formation, and mentoring.',
          icon: 'ri-graduation-cap-line',
          focus: 'Next Generation Empowerment'
        },
        {
          name: 'Showbiz & Influence Forum',
          description: 'Supporting artists, influencers, and athletes in aligning purpose with public impact and cultural transformation.',
          icon: 'ri-star-line',
          focus: 'Purpose-Driven Influence'
        }
      ]
    }
  ];

  return (
    <>
      <SEOHead
        title="Charter Forums | Wealth Relationships Legacy | The CROWN Abundance Movement"
        description="Explore The CROWN Abundance Movement three charter pillars: Wealth Mastery, Relationship Wholeness, and Generational Legacy. Join specialized forums for entrepreneurs, founders, investors, families, and youth."
        keywords="wealth mastery charter, relationship wholeness charter, generational legacy charter, founders forum, entrepreneurs forum, investors forum, singles forum, married forum"
        canonicalPath="/charters"
        type="WebPage"
        jsonLd={jsonLd}
      />
      <div className="min-h-screen" style={{ background: '#3D2C8D' }}>
        {/* Hero Section */}
        <section className="relative py-32 overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src="https://readdy.ai/api/search-image?query=Elegant%20conference%20hall%20with%20diverse%20African%20American%20and%20Latino%20leaders%20in%20professional%20attire%2C%20golden%20chandeliers%2C%20purple%20and%20gold%20color%20scheme%2C%20sophisticated%20networking%20event%20atmosphere%2C%20warm%20ambient%20lighting%2C%20high-end%20corporate%20gathering%2C%20no%20white%20people%2C%20only%20Black%20and%20Latino%20professionals&width=1920&height=800&seq=charters-hero-v2&orientation=landscape"
              alt="Charter Forums"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-purple-900/95 via-purple-900/85 to-purple-800/75"></div>
          </div>
          
          <div className="absolute inset-0">
            <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-amber-400/20 to-yellow-300/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-r from-amber-500/15 to-yellow-400/15 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="absolute bottom-32 left-1/3 w-40 h-40 bg-gradient-to-r from-amber-300/10 to-yellow-200/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <div className="inline-block p-4 bg-gradient-to-r from-amber-400 to-yellow-300 rounded-2xl mb-8 transform hover:scale-110 transition-transform duration-300">
              <i className="ri-community-line text-4xl text-white"></i>
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-bold mb-8" style={{ fontFamily: '"Playfair Display", serif', color: '#F5A623' }}>
              Charter-Based
              <span className="block text-6xl lg:text-8xl bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">
                Forums
              </span>
            </h1>
            
            <p className="text-xl max-w-4xl mx-auto leading-relaxed mb-12" style={{ color: '#FFFFFF' }}>
              The CROWN Abundance Movement is structured into three primary pillars — Wealth, Relationships, and Generational Legacy — each hosting specialized forums for targeted transformation and community support.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              {charters.map((charter) => (
                <a 
                  key={charter.id}
                  href={`#${charter.id}`}
                  className={`px-6 py-3 rounded-full bg-gradient-to-r ${charter.gradient} text-white font-medium hover:opacity-90 transition-all duration-300 transform hover:scale-105 cursor-pointer whitespace-nowrap`}
                >
                  <i className={`${charter.icon} mr-2`}></i>
                  {charter.title.split(' ')[0]}
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Charter Sections */}
        {charters.map((charter, index) => (
          <section key={charter.id} id={charter.id} className="py-20">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <div className="absolute inset-0">
                  <img 
                    src={charter.bgImage}
                    alt={charter.title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-900/90 to-purple-800/80"></div>
                </div>

                <div className="relative z-10 p-12 lg:p-16">
                  <div className="text-center mb-12">
                    <div className={`inline-block p-4 rounded-full bg-gradient-to-r ${charter.gradient} mb-6 transform hover:scale-110 transition-transform duration-300`}>
                      <i className={`${charter.icon} text-4xl text-white`}></i>
                    </div>
                    <h2 className="text-4xl lg:text-5xl font-bold mb-4" style={{ fontFamily: '"Playfair Display", serif', color: '#F5A623' }}>
                      {charter.title}
                    </h2>
                    <p className="text-xl leading-relaxed max-w-3xl mx-auto" style={{ color: '#FFFFFF' }}>
                      {charter.subtitle}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {charter.forums.map((forum, forumIndex) => (
                      <div 
                        key={forumIndex}
                        className="group bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-amber-400/20 hover:bg-white/15 transition-all duration-300 transform hover:-translate-y-2"
                      >
                        <div className="text-center mb-6">
                          <div className={`w-16 h-16 bg-gradient-to-r ${charter.gradient} rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                            <i className={`${forum.icon} text-2xl text-white`}></i>
                          </div>
                          <h3 className="text-xl font-bold mb-2" style={{ color: '#F5A623' }}>
                            {forum.name}
                          </h3>
                          <div className="inline-block px-3 py-1 rounded-full text-xs font-medium mb-4" style={{ background: 'rgba(245, 166, 35, 0.2)', color: '#F5A623' }}>
                            {forum.focus}
                          </div>
                        </div>
                        
                        <p className="text-sm leading-relaxed text-center" style={{ color: '#FFFFFF' }}>
                          {forum.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        ))}

        {/* Call to Action */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl p-12 lg:p-16">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-yellow-300"></div>
              <div className="absolute inset-0">
                <div className="absolute top-10 right-10 w-32 h-32 bg-white/20 rounded-full blur-2xl animate-pulse"></div>
                <div className="absolute bottom-10 left-10 w-24 h-24 bg-white/15 rounded-full blur-xl animate-pulse" style={{ animationDelay: '1s' }}></div>
              </div>
              
              <div className="relative z-10">
                <h2 className="text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: '"Playfair Display", serif', color: '#3D2C8D' }}>
                  Ready to Join Your Forum?
                </h2>
                <p className="text-lg mb-8" style={{ color: '#3D2C8D' }}>
                  Find your community within The CROWN Abundance Movement and begin your transformation journey today.
                </p>
                <a 
                  href="/membership"
                  className="inline-block px-8 py-4 rounded-full font-medium text-white transition-all duration-300 transform hover:scale-105 cursor-pointer whitespace-nowrap"
                  style={{ background: '#3D2C8D' }}
                >
                  <i className="ri-user-add-line mr-2"></i>
                  Explore Membership
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
